/**
 * CreateRouteModal - Modal for creating a new route (Routes__c)
 */

import { useState, useEffect } from 'react';
import { useCalendarStore } from '../../stores/calendar-store';
import { salesforceClient } from '../../lib/salesforce';
import type { MasterRouteDto } from '../../lib/salesforce';

interface CreateRouteModalProps {
  datum: string; // YYYY-MM-DD
  onClose: () => void;
}

export function CreateRouteModal({ datum, onClose }: CreateRouteModalProps) {
  const { chauffeurs, createRoute } = useCalendarStore();
  const [routeId, setRouteId] = useState('');
  const [chauffeurId, setChauffeurId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [masterRoutes, setMasterRoutes] = useState<MasterRouteDto[]>([]);

  useEffect(() => {
    salesforceClient.getMasterRoutes().then(setMasterRoutes).catch(console.error);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      await createRoute({
        datum,
        routeId: routeId || undefined,
        chauffeurId: chauffeurId || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kon route niet aanmaken');
    } finally {
      setIsSaving(false);
    }
  };

  // Format date for display
  const displayDate = new Date(datum + 'T00:00:00').toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Route toevoegen
        </h3>
        <p className="text-sm text-gray-500 mb-4">{displayDate}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Route */}
            <div>
              <label htmlFor="route" className="block text-sm font-medium text-gray-700 mb-1">
                Route
              </label>
              <select
                id="route"
                value={routeId}
                onChange={(e) => setRouteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                autoFocus
              >
                <option value="">Geen route</option>
                {masterRoutes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chauffeur */}
            <div>
              <label htmlFor="chauffeur" className="block text-sm font-medium text-gray-700 mb-1">
                Chauffeur
              </label>
              <select
                id="chauffeur"
                value={chauffeurId}
                onChange={(e) => setChauffeurId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                <option value="">Geen chauffeur</option>
                {chauffeurs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Opslaan...
                </>
              ) : (
                'Opslaan'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
